import React, {
  InputHTMLAttributes,
  PropsWithChildren,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, ListItem, Text, UnorderedList } from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { DropZone, HiddenInput, RemoveFile, DropText } from "./FileInputStyles";

const isDropEvent = (event: React.SyntheticEvent): event is React.DragEvent =>
  event.type === "drop";

const preventDefault = (event: React.SyntheticEvent) => event.preventDefault();

interface Props {
  onChange: (files: File[]) => void;
  input?: Omit<InputHTMLAttributes<HTMLInputElement>, "onChange">;
  box?: object;
  noConfirm?: boolean;
  confirmText?: string;
}
export const FileInput = forwardRef<HTMLInputElement, PropsWithChildren<Props>>(
  (
    {
      onChange,
      children,
      input,
      box,
      noConfirm = false,
      confirmText = "Verzenden",
    },
    ref
  ) => {
    const [files, setFiles] = useState<File[]>([]);
    const labelRef = useRef<HTMLLabelElement>(null);
    const id = useId();

    const validTypes = useMemo(
      () => input?.accept?.split(/\s*,\s*/),
      [input?.accept]
    );

    const getValidItems = useCallback(
      <TItemType extends DataTransferItem | File = DataTransferItem | File>(
        items: Iterable<TItemType>
      ): TItemType[] =>
        Array.from(items as Iterable<TItemType>).filter(
          (item) =>
            !validTypes ||
            validTypes.some((t) =>
              t.includes("*")
                ? new RegExp(t.replace("*", "[^/*]+")).test(item.type)
                : validTypes.includes(item.type)
            )
        ),
      [validTypes]
    );

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement> | React.DragEvent) => {
        labelRef.current?.classList.remove("canDrop", "invalidDrop");

        const inputDOMNode = document.getElementById(id) as HTMLInputElement;

        if (isDropEvent(event)) {
          event.preventDefault();

          if (!event.dataTransfer.files.length) return;

          const validFiles = getValidItems(event.dataTransfer.files);

          if (!input?.multiple) {
            validFiles.splice(1);
          }

          setFiles(validFiles);
        } else {
          setFiles(getValidItems(inputDOMNode.files ?? []));
        }
      },
      [input?.multiple, id, getValidItems]
    );

    const handleDragEnter = useCallback(
      (e: React.DragEvent) => {
        const validItems = getValidItems(e.dataTransfer.items);

        if (e.dataTransfer.items.length && validItems.length) {
          labelRef.current?.classList.add("canDrop");
          labelRef.current?.classList.remove("invalidDrop");
        } else {
          if (!e.dataTransfer.items.length) return;
          labelRef.current?.classList.remove("canDrop");
          labelRef.current?.classList.add("invalidDrop");
        }
      },
      [getValidItems]
    );

    const handleDragLeave = useCallback((e: React.DragEvent) => {
      if (
        !(e.relatedTarget instanceof Node) ||
        !e.currentTarget.contains(e.relatedTarget)
      ) {
        labelRef.current?.classList.remove("canDrop", "invalidDrop");
      }
    }, []);

    const onConfirm = useCallback(() => {
      onChange(files);
      setFiles([]);
    }, [files, onChange]);

    useEffect(() => {
      if (noConfirm && files.length) {
        onConfirm();
      }
    }, [noConfirm, files.length, onConfirm]);

    const confirm = useCallback(
      (event: React.MouseEvent) => {
        event.preventDefault();
        onConfirm();
      },
      [onConfirm]
    );

    return (
      <DropZone
        ref={labelRef}
        as="label"
        background="neutral200"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={preventDefault}
        onDrop={handleChange}
        style={{ display: "block" }}
        {...box}
      >
        <HiddenInput
          {...input}
          ref={ref}
          id={id}
          onChange={handleChange}
          type="file"
        />
        {(!files.length || input?.multiple) && (
          <Button mb={4} mt={4} onClick={() => labelRef.current?.click()}>
            {children}
          </Button>
        )}
        {!!files?.length && (
          <>
            <UnorderedList>
              {Array.from(files, (file, i) => (
                <ListItem key={`${file.name}-${i}`}>
                  {file.name}
                  <RemoveFile
                    onClick={(e: React.UIEvent) => {
                      setFiles((current) => current.filter((f) => f !== file));
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  />
                </ListItem>
              ))}
            </UnorderedList>
            {!noConfirm && (
              <Button marginTop={4} onClick={confirm}>
                <CheckIcon mr={2} />
                <Text>{confirmText}</Text>
              </Button>
            )}
          </>
        )}
        <DropText>
          <Text>Zet hier je bestanden neer of klik om te uploaden</Text>
        </DropText>
      </DropZone>
    );
  }
);
FileInput.displayName = "FileInput";
